import React, {useEffect, useState} from 'react';
import TeacherLayout from "@/components/layout/teacher/teacherLayout";
import {URLS} from "@/lib/urls";
import {Link, useParams} from "react-router-dom";
import {Avatar, Button, Input, InputNumber, Popconfirm, Tag, Typography} from "antd";
import {
    BookOutlined,
    CalendarOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    CloseCircleOutlined,
    FileTextOutlined,
    IdcardOutlined,
    MessageOutlined,
    StarOutlined,
    UserOutlined
} from "@ant-design/icons";
import {useApiGetTeacherHomeworksReports, useApiTeacherHomeworkReportUpdate} from "@/api/homework";
import {defaultAvatar} from "@/lib/helper/defaultAvatar";
import toFaNumber from "@/lib/utils/number";
import {formatDateFull} from "@/lib/utils/date";
import ApiHandleSuccessMessage, {ApiHandleErrorMessage} from "@/lib/helper/apiHandleResultMessage";
import RenderLoading from "@/components/render-if-loading";
import {sprintf} from "@/lib/utils/sprintf";
import FileViewer from "@/lib/helper/FileViewer";
import RenderIf from "@/components/render-if";

const {Text, Paragraph} = Typography;
const {TextArea} = Input;

function TeacherHomeworkEditPage(props) {
    const {id} = useParams();

    const [editingNote, setEditingNote] = useState(false);
    const [noteValue, setNoteValue] = useState();
    const [scoreValue, setScoreValue] = useState();
    const [editingScore, setEditingScore] = useState(false);

    const {data: homeworkReportDetail, isPending, refetch} = useApiGetTeacherHomeworksReports({id: id})
    const {mutate: updateHomeworkReport, isPending: isPendingUpdateHomeworkReport} = useApiTeacherHomeworkReportUpdate()

    useEffect(() => {
        setScoreValue(homeworkReportDetail?.data?.data?.score)
        setNoteValue(homeworkReportDetail?.data?.data?.advice)
    }, [homeworkReportDetail?.data?.data])

    const statusConfig = {
        pending: {
            color: 'text-amber-700 dark:text-amber-400',
            bgColor: 'bg-amber-50 dark:bg-amber-950/30',
            borderColor: 'border-amber-200 dark:border-amber-800',
            label: 'در انتظار بررسی',
            icon: <ClockCircleOutlined/>
        },
        accepted: {
            color: 'text-green-700 dark:text-green-400',
            bgColor: 'bg-green-50 dark:bg-green-950/30',
            borderColor: 'border-green-200 dark:border-green-800',
            label: 'تایید شده',
            icon: <CheckCircleOutlined/>
        },
        rejected: {
            color: 'text-red-700 dark:text-red-400',
            bgColor: 'bg-red-50 dark:bg-red-950/30',
            borderColor: 'border-red-200 dark:border-red-800',
            label: 'رد شده',
            icon: <CloseCircleOutlined/>
        },
    };

    const handleApprove = () => {
        handleUpdateHomeworkReport({status: 'accepted'})
    };

    const handleReject = () => {
        handleUpdateHomeworkReport({status: 'rejected'})
    };

    const handleSaveNote = () => {
        handleUpdateHomeworkReport({advice: noteValue})
        setEditingNote(false)
    };

    const handleSaveScore = () => {
        handleUpdateHomeworkReport({score: scoreValue})
        setEditingScore(false)
    };

    function handleUpdateHomeworkReport(args) {
        updateHomeworkReport(
            {
                ...args,
                report_id: id,
                id: homeworkReportDetail?.data?.data?.homework_id
            },
            {
                onSuccess: (r) => {
                    ApiHandleSuccessMessage(r)
                    refetch()
                },
                onError: (e) => {
                    ApiHandleErrorMessage(e)
                }
            });
    }

    const cfg = statusConfig[homeworkReportDetail?.data?.data?.status];

    const getScoreColor = (score) => {
        if (score > homeworkReportDetail?.data?.data?.homework?.score / 2) return 'text-green-600 dark:text-green-400';
        if (score > homeworkReportDetail?.data?.data?.homework?.score / 4) return 'text-yellow-600 dark:text-yellow-400';
        return 'text-red-600 dark:text-red-400';
    };

    return (
        <TeacherLayout
            backTo={URLS.teacher.homeworks.dashboard}
            pageTitle={'بررسی تکلیف' + ' #' + id}
        >
            <RenderLoading loading={isPending} skeleton={true}>
                <div className="max-w-7xl mx-auto p-4 lg:p-6">

                    {/* HEADER */}
                    <div className="
                     border dark:border-gray-700 border-gray-200
                    rounded-3xl p-5 lg:p-6 mb-6
                ">
                        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">

                            {/* student */}
                            <div className="flex flex-wrap items-center gap-4 min-w-0">
                                <Avatar
                                    src={
                                        homeworkReportDetail?.data?.data?.student_detail?.avatar ??
                                        defaultAvatar()
                                    }
                                    size={72}
                                    className="
                                    border-4 border-blue-50 dark:border-blue-950
                                    flex-shrink-0
                                "
                                />

                                <div className="min-w-0">
                                    <h1 className="
                                    text-xl lg:text-2xl
                                    font-black text-gray-900 dark:text-gray-100
                                    truncate
                                ">
                                        {homeworkReportDetail?.data?.data?.student_detail?.firstname}{' '}
                                        {homeworkReportDetail?.data?.data?.student_detail?.lastname}
                                    </h1>

                                    <div className="
                                    flex flex-wrap items-center
                                    gap-x-4 gap-y-2
                                    mt-3
                                    text-sm text-gray-600 dark:text-gray-400
                                ">
                                        <div className="flex items-center gap-1">
                                            <IdcardOutlined/>
                                            <span>
                                                {homeworkReportDetail?.data?.data.student_detail?.code}
                                            </span>
                                        </div>

                                        <div className="flex items-center gap-1">
                                            <CalendarOutlined/>
                                            <span>
                                                {formatDateFull(
                                                    homeworkReportDetail?.data?.data.created_at
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* status */}
                            <div className="flex flex-wrap flex-col items-start lg:items-end gap-3">
                                <div
                                    className={`
                                    ${cfg?.bgColor}
                                    ${cfg?.borderColor}
                                    ${cfg?.color}
                                    border rounded-2xl
                                    px-4 py-2
                                    flex items-center gap-2
                                    font-semibold text-sm
                                `}
                                >
                                    {cfg?.icon}
                                    {cfg?.label}
                                </div>

                                <Link
                                    to={sprintf(URLS.teacher.lessons.detail, {
                                        id: homeworkReportDetail?.data?.data?.homework?.lesson?.id
                                    })}
                                >
                                    <Tag
                                        icon={<BookOutlined/>}
                                        color="blue"
                                        className="
                                        text-wrap
                                        rounded-full
                                        px-3 py-1
                                        text-sm
                                        m-0
                                    "
                                    >
                                        {
                                            homeworkReportDetail?.data?.data
                                                ?.homework?.lesson?.topic?.title
                                        }
                                    </Tag>
                                </Link>
                            </div>
                        </div>
                    </div>

                    {/* MAIN GRID */}
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

                        {/* CONTENT */}
                        <div className="lg:col-span-8 space-y-6">

                            {/* HOMEWORK */}
                            <section className="
                             border dark:border-gray-700 border-gray-200
                            rounded-3xl p-5 lg:p-6
                        ">
                                <div className="flex items-start gap-3 mb-5">
                                    <div className="
                                    w-11 h-11 rounded-2xl
                                    bg-blue-50 dark:bg-blue-950/40
                                    flex items-center justify-center
                                    flex-shrink-0
                                ">
                                        <FileTextOutlined className="text-blue-600 dark:text-blue-400 text-lg"/>
                                    </div>

                                    <div>
                                        <h2 className="font-black text-gray-900 dark:text-gray-100 text-lg">
                                            عنوان تکلیف
                                        </h2>

                                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                            توضیحات و محتوای تکلیف
                                        </p>
                                    </div>
                                </div>

                                <div>
                                    <h3 className="
                                    text-lg font-bold text-gray-900 dark:text-gray-100
                                    mb-4
                                ">
                                        {
                                            homeworkReportDetail?.data?.data
                                                ?.homework?.title
                                        }
                                    </h3>

                                    <div className="
                                    leading-8 text-gray-700 dark:text-gray-300
                                    whitespace-pre-line
                                ">
                                        {
                                            homeworkReportDetail?.data?.data
                                                ?.homework?.description
                                        }
                                    </div>
                                </div>
                            </section>

                            {/* STUDENT ANSWER */}
                            <section className="
                            bg-gradient-to-b
                            from-gray-50 to-white
                            dark:from-gray-800/60 dark:to-gray-900/40
                            border dark:border-gray-700 border-gray-200
                            rounded-3xl
                            p-5 lg:p-6
                        ">
                                <div className="flex items-start gap-3 mb-6">
                                    <div className="
                                    w-11 h-11 rounded-2xl
                                    bg-gray-100 dark:bg-gray-700
                                    flex items-center justify-center
                                    flex-shrink-0
                                ">
                                        <UserOutlined className="text-gray-600 dark:text-gray-300 text-lg"/>
                                    </div>

                                    <div>
                                        <h2 className="
                                        font-black text-gray-900 dark:text-gray-100 text-lg
                                    ">
                                            پاسخ دانش‌آموز
                                        </h2>

                                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                            پاسخ ارسال شده برای این تکلیف
                                        </p>
                                    </div>
                                </div>

                                <div className="
                                bg-white dark:bg-gray-800
                                border dark:border-gray-700 border-gray-200
                                rounded-2xl
                                p-5
                            ">
                                    <div className="
                                    leading-9
                                    text-[15px] text-gray-700 dark:text-gray-300
                                    whitespace-pre-line
                                ">
                                        <RenderIf condition={homeworkReportDetail?.data?.data?.file}>
                                            <FileViewer fileUrl={homeworkReportDetail?.data?.data?.file}/>
                                        </RenderIf>
                                        {homeworkReportDetail?.data?.data?.report}
                                    </div>
                                </div>
                            </section>

                            {/* TEACHER NOTE */}
                            <section className="
                             border dark:border-gray-700 border-gray-200
                            rounded-3xl p-5 lg:p-6
                        ">
                                <div className="flex items-start gap-3 mb-5">
                                    <div className="
                                    w-11 h-11 rounded-2xl
                                    bg-blue-50 dark:bg-blue-950/40
                                    flex items-center justify-center
                                    flex-shrink-0
                                ">
                                        <MessageOutlined className="text-blue-600 dark:text-blue-400 text-lg"/>
                                    </div>

                                    <div>
                                        <h2 className="
                                        font-black text-gray-900 dark:text-gray-100 text-lg
                                    ">
                                            بازخورد استاد
                                        </h2>

                                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                            نظر و توضیحات خود را ثبت کنید
                                        </p>
                                    </div>
                                </div>

                                <Input.TextArea
                                    value={noteValue}
                                    onChange={(e) => setNoteValue(e.target.value)}
                                    rows={7}
                                    placeholder="یادداشت و بازخورد خود را بنویسید..."
                                    className="
                                    rounded-2xl
                                    !py-3
                                    text-sm
                                    dark:!bg-gray-800
                                    dark:!border-gray-600
                                    dark:!text-gray-100
                                    dark:placeholder:!text-gray-500
                                "
                                />

                                <div className="mt-4 flex justify-end">
                                    <Button
                                        type="primary"
                                        size="large"
                                        onClick={handleSaveNote}
                                        loading={isPendingUpdateHomeworkReport}
                                        className="
                                        h-11 px-6
                                        rounded-2xl
                                        font-bold
                                    "
                                    >
                                        ذخیره بازخورد
                                    </Button>
                                </div>
                            </section>
                        </div>

                        {/* SIDEBAR */}
                        <div className="lg:col-span-4">
                            <div className="sticky top-5 space-y-5">

                                {/* SCORE */}
                                <section className="
                                 border dark:border-gray-700 border-gray-200
                                rounded-3xl p-5
                            ">
                                    <div className="
                                    flex items-center gap-2
                                    mb-5
                                ">
                                        <StarOutlined className="text-yellow-500"/>
                                        <h3 className="font-black text-gray-900 dark:text-gray-100">
                                            نمره‌دهی
                                        </h3>
                                    </div>

                                    <div className="text-center mb-6">
                                        <div
                                            className={`
                                            text-6xl
                                            font-black
                                            tracking-tight
                                            leading-none
                                            ${getScoreColor(
                                                homeworkReportDetail?.data?.data?.score
                                            )}
                                        `}
                                        >
                                            {
                                                homeworkReportDetail?.data?.data?.score ??
                                                '-'
                                            }
                                        </div>

                                        <div className="
                                        text-gray-500 dark:text-gray-400
                                        mt-2 text-lg
                                    ">
                                            از {
                                            toFaNumber(
                                                homeworkReportDetail?.data?.data
                                                    ?.homework?.score
                                            )
                                        }
                                        </div>
                                    </div>

                                    <InputNumber
                                        min={0}
                                        max={
                                            homeworkReportDetail?.data?.data
                                                ?.homework?.score
                                        }
                                        value={scoreValue}
                                        onChange={(v) => setScoreValue(v)}
                                        size="large"
                                        className="w-full"
                                        placeholder="ثبت نمره"
                                    />

                                    <Button
                                        type="primary"
                                        block
                                        size="large"
                                        onClick={handleSaveScore}
                                        loading={isPendingUpdateHomeworkReport}
                                        className="
                                        mt-4 h-12
                                        rounded-2xl
                                        font-bold
                                    "
                                    >
                                        ذخیره نمره
                                    </Button>
                                </section>

                                {/* ACTIONS */}
                                <section className="
                                 border dark:border-gray-700 border-gray-200
                                rounded-3xl p-5
                            ">
                                    <h3 className="
                                    font-black text-gray-900 dark:text-gray-100
                                    mb-5
                                ">
                                        وضعیت تکلیف
                                    </h3>

                                    {homeworkReportDetail?.data?.data.status === 'pending' ? (
                                        <div className="space-y-3">
                                            <Popconfirm
                                                title="تایید تکلیف"
                                                description="آیا از تایید این تکلیف اطمینان دارید؟"
                                                onConfirm={handleApprove}
                                                okText="تایید"
                                                cancelText="انصراف"
                                            >
                                                <Button
                                                    loading={isPendingUpdateHomeworkReport}
                                                    type="primary"
                                                    icon={<CheckCircleOutlined/>}
                                                    size="large"
                                                    className="
                                                    w-full h-12
                                                    rounded-2xl
                                                    bg-green-500
                                                    border-green-500
                                                    hover:!bg-green-600
                                                    hover:!border-green-600
                                                    font-bold
                                                "
                                                >
                                                    تایید تکلیف
                                                </Button>
                                            </Popconfirm>

                                            <Popconfirm
                                                title="رد تکلیف"
                                                description="آیا از رد این تکلیف اطمینان دارید؟"
                                                onConfirm={handleReject}
                                                okText="رد تکلیف"
                                                cancelText="انصراف"
                                            >
                                                <Button
                                                    danger
                                                    loading={isPendingUpdateHomeworkReport}
                                                    icon={<CloseCircleOutlined/>}
                                                    size="large"
                                                    className="
                                                    w-full h-12
                                                    rounded-2xl
                                                    font-bold
                                                "
                                                >
                                                    رد تکلیف
                                                </Button>
                                            </Popconfirm>
                                        </div>
                                    ) : (
                                        <Button
                                            block
                                            size="large"
                                            loading={isPendingUpdateHomeworkReport}
                                            icon={<ClockCircleOutlined/>}
                                            onClick={() =>
                                                handleUpdateHomeworkReport({
                                                    status: 'pending'
                                                })
                                            }
                                            className="
                                            h-12 rounded-2xl
                                            font-bold
                                        "
                                        >
                                            بازگشت به حالت بررسی
                                        </Button>
                                    )}
                                </section>
                            </div>
                        </div>
                    </div>
                </div>
            </RenderLoading>
        </TeacherLayout>
    );
}

export default TeacherHomeworkEditPage;